import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/stock-levels")({
  component: StockLevelsPage,
});

import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { BarcodeScanControl } from "@/components/inventory/barcode/BarcodeScanControl";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import {
  useFetchStockLevels,
  type StockLevelOrdering,
} from "@/hooks/api/inventory/useStockOps";
import {
  useFetchWarehouses,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { useFetchCollections } from "@/hooks/api/inventory/useCollectionsLabels";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { ItemSearchFilter } from "@/components/inventory/shared/ItemSearchFilter";
import { FolderFilterSection } from "@/components/inventory/shared/FolderFilterSection";
import { StockLevelTable } from "@/components/inventory/tables/StockLevelTable";
import { StockLevelSheet } from "@/components/inventory/forms/StockLevelSheet";
import instance from "@/lib/axios";
import { getInventoryUrl } from "@/lib/inventory";
import type { StockLevel, Variant } from "@/types/inventory";

function StockLevelsPage() {
  const navigate = useNavigate();

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [itemFilter, setItemFilter] = useState<string | undefined>(undefined);
  const [variantFilter, setVariantFilter] = useState<string | undefined>(
    undefined
  );
  const [itemVariants, setItemVariants] = useState<Variant[]>([]);
  const [showFolders, setShowFolders] = useState(true);
  const [selectedCollection, setSelectedCollection] = useState<number | null>(
    null
  );

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        item__name: SortDirection;
        warehouse__name: SortDirection;
        quantity: SortDirection;
        minimum_level: SortDirection;
        reorder_point: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      StockLevelOrdering
    >({
      item__name: null,
      warehouse__name: null,
      quantity: null,
      minimum_level: null,
      reorder_point: null,
      created_at: null,
      updated_at: null,
    });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [selected, setSelected] = useState<StockLevel | null>(null);

  const stockLevelsQuery = useFetchStockLevels({
    page,
    page_size: pageSize,
    search: searchFilter || undefined,
    warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
    item: itemFilter ? Number(itemFilter) : undefined,
    variant: variantFilter ? Number(variantFilter) : undefined,
    collection: selectedCollection ?? undefined,
    ordering: getApiOrdering(),
  });
  const stockLevels = stockLevelsQuery.data?.results ?? [];
  const count = stockLevelsQuery.data?.count ?? 0;
  const loading = stockLevelsQuery.isPending;
  const isFetching = stockLevelsQuery.isFetching;

  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];

  const collectionsQuery = useFetchCollections(ALL_ACTIVE_PARAMS);
  const collections = collectionsQuery.data?.results ?? [];

  const selectCollection = (id: number | null) => {
    setSelectedCollection(id);
    setPage(1);
  };

  const folderTitle = selectedCollection
    ? (collections.find((c) => c.id === selectedCollection)?.name ?? "Folder")
    : "Stock levels outside of folders";

  useEffect(() => {
    if (itemFilter) {
      instance
        .get<{ variants: Variant[] }>(getInventoryUrl(`items/${itemFilter}/`))
        .then((res) => setItemVariants(res.data.variants ?? []))
        .catch(() => setItemVariants([]));
    } else {
      setItemVariants([]);
    }
    setVariantFilter(undefined);
  }, [itemFilter]);

  const resetFilters = () => {
    setSearchFilter("");
    setWarehouseFilter(undefined);
    setItemFilter(undefined);
    setVariantFilter(undefined);
    setItemVariants([]);
    setSelectedCollection(null);
    setOrdering({
      item__name: null,
      warehouse__name: null,
      quantity: null,
      minimum_level: null,
      reorder_point: null,
      created_at: null,
      updated_at: null,
    });
    setPage(1);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Stock Levels"
          description="View current stock quantities across all warehouses"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          isLoading={isFetching}
        />

        <FolderFilterSection
          collections={collections}
          isPending={collectionsQuery.isPending}
          selectedCollection={selectedCollection}
          onSelectCollection={selectCollection}
          showFolders={showFolders}
          onToggleShowFolders={() => setShowFolders((v) => !v)}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search item or SKU..."
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 pl-8"
            />
          </div>
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => {
              setWarehouseFilter(v);
              setPage(1);
            }}
          />
          <ItemSearchFilter
            value={itemFilter}
            onChange={(id) => {
              setItemFilter(id == null ? undefined : String(id));
              setPage(1);
            }}
          />
          {itemVariants.length > 0 && (
            <SelectFilter
              placeholder="Variant"
              options={itemVariants.map((v) => ({
                label: `${v.is_default ? "Default" : v.option_values.map((ov) => ov.value_text).join(" / ")} (${v.sku})`,
                value: String(v.id),
              }))}
              value={variantFilter}
              onChange={(v) => {
                setVariantFilter(v);
                setPage(1);
              }}
            />
          )}
          <BarcodeScanControl
            onScan={(code) => {
              setSearchFilter(code.trim());
              setPage(1);
            }}
            dialogDescription="Point the camera at an item or variant barcode to filter stock levels to it."
          />
        </FiltersPanel>

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">{folderTitle}</h2>
              {selectedCollection && (
                <Badge
                  variant="muted"
                  className="cursor-pointer text-xs"
                  onClick={() => selectCollection(null)}
                >
                  Clear ×
                </Badge>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <StockLevelTable
              stockLevels={stockLevels}
              isLoading={loading}
              ordering={ordering}
              onOrderingChange={toggleOrdering}
              onItemClick={(row) =>
                navigate({
                  to: "/admin/inventory/items/$id",
                  params: { id: String(row.item) },
                })
              }
              onView={(row) => {
                setSelected(row);
                setIsViewOnly(true);
                setIsSheetOpen(true);
              }}
              onEdit={(row) => {
                setSelected(row);
                setIsViewOnly(false);
                setIsSheetOpen(true);
              }}
            />
            <PaginationComponent
              count={count}
              page={page}
              onPageChange={(p) => setPage(p)}
              isLoading={isFetching}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </div>
        </div>
      </div>

      <StockLevelSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        record={selected}
        isViewOnly={isViewOnly}
      />
    </SidebarInset>
  );
}
